import { useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { ECharts } from 'echarts';
import type { IOpinionEventFloaterDetail } from '../OpinionEventFloater';
import { useOpinionDateAxisPointerSync } from './useOpinionDateAxisPointerSync';

const FLOATER_HIDE_DELAY_MS = 50;

function isMovingToNode(
  node: HTMLElement | null,
  relatedTarget: EventTarget | null,
): boolean {
  if (!(relatedTarget instanceof Node) || !node) {
    return false;
  }
  return node === relatedTarget || node.contains(relatedTarget);
}

interface IUseOpinionChartEventItemHoverOptions {
  myChart: ECharts | null;
  xAxisData: string[];
  showEventAxis: boolean;
  xAxisValue: string;
  bubbleEventDetail?: IOpinionEventFloaterDetail | null;
  eventBoxRef: RefObject<HTMLDivElement>;
  eventLineRef: RefObject<HTMLDivElement>;
  setEventLineShow: (value: boolean) => void;
  onLeaveMarkArea: () => void;
}

export function useOpinionChartEventItemHover({
  myChart,
  xAxisData,
  showEventAxis,
  xAxisValue,
  bubbleEventDetail,
  eventBoxRef,
  eventLineRef,
  setEventLineShow,
  onLeaveMarkArea,
}: IUseOpinionChartEventItemHoverOptions) {
  const [showEventFloater, setShowEventFloater] = useState(false);
  const isMouseOverFloater = useRef(false);
  const hideFloaterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    sync,
    scheduleClear,
    setSuppressAxisPointer,
    setKeepAxisPointer,
  } = useOpinionDateAxisPointerSync(myChart, xAxisData, showEventAxis);

  const clearHideFloaterTimer = (): void => {
    if (hideFloaterTimer.current) {
      clearTimeout(hideFloaterTimer.current);
      hideFloaterTimer.current = null;
    }
  };

  const showDetailFloater = (): void => {
    if (bubbleEventDetail?.event_list) {
      setShowEventFloater(true);
    }
  };

  const handleEventBubbleEnter = (): void => {
    clearHideFloaterTimer();
    setKeepAxisPointer(false);
    setSuppressAxisPointer(true);
    setEventLineShow(true);
    showDetailFloater();
  };

  const handleEventBubbleLeave = (
    event?: React.MouseEvent,
  ): void => {
    const relatedTarget = event?.relatedTarget ?? null;
    if (isMovingToNode(eventLineRef.current, relatedTarget)) {
      setShowEventFloater(false);
      setSuppressAxisPointer(false);
      setKeepAxisPointer(true);
      sync(xAxisValue);
      return;
    }

    onLeaveMarkArea();
    clearHideFloaterTimer();
    setSuppressAxisPointer(false);
    scheduleClear();
    hideFloaterTimer.current = setTimeout(() => {
      if (!isMouseOverFloater.current) {
        setShowEventFloater(false);
      }
    }, FLOATER_HIDE_DELAY_MS);
  };

  const handleEventLineEnter = (): void => {
    clearHideFloaterTimer();
    setSuppressAxisPointer(false);
    setKeepAxisPointer(true);
    setEventLineShow(true);
    setShowEventFloater(false);
    sync(xAxisValue);
  };

  const handleEventLineLeave = (event?: React.MouseEvent): void => {
    const relatedTarget = event?.relatedTarget ?? null;
    if (isMovingToNode(eventBoxRef.current, relatedTarget)) {
      setKeepAxisPointer(false);
      handleEventBubbleEnter();
      return;
    }

    setKeepAxisPointer(false);
    setEventLineShow(false);
    scheduleClear();
  };

  const floaterPointerHandlers = {
    onMouseEnter: (): void => {
      isMouseOverFloater.current = true;
      clearHideFloaterTimer();
      setSuppressAxisPointer(true);
    },
    onMouseLeave: (): void => {
      isMouseOverFloater.current = false;
      setSuppressAxisPointer(false);
      setShowEventFloater(false);
      scheduleClear();
    },
  };

  return {
    showEventFloater: showEventFloater && !!bubbleEventDetail,
    handleEventBubbleEnter,
    handleEventBubbleLeave,
    handleEventLineEnter,
    handleEventLineLeave,
    floaterPointerHandlers,
  };
}
